import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  cloneDeep,
  difference,
  each,
  filter,
  find,
  findIndex,
  findKey,
  isEqual,
  map
} from 'lodash';

import * as Color from 'tinycolor2';
import { Dimmer, Loader } from 'semantic-ui-react';
import { ApplyZoom, RemoveZoom } from '../../../../../lib/Zoom';

const iterator = array => {
  const clone = [...array];
  return () => {
    return clone.shift();
  };
};

const onImageReady = (image, fct) => {
  if (image.complete) {
    fct();
  } else {
    image.onload = fct;
  }
};
const saveCanvasToImage = (canvas, targetImg, displayWidth) => {
  return done => {
    const id = canvas.toDataURL();

    const img = targetImg;

    const imageResizeRatio = canvas.width / canvas.height;
    img.width = displayWidth;
    img.height = Math.round(displayWidth / imageResizeRatio);

    img.src = id;

    done();
  };
};

const getImageDrawer = (canvas2dContext, image) => {
  return done => {
    onImageReady(image, () => {
      canvas2dContext.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);
      done();
    });
  };
};

const getGradient = (startColor, endColor, steps) => {
  const gradient = [];

  const rgb1 = Color(startColor).toRgb();
  const rgb2 = Color(endColor).toRgb();
  const diffRed = rgb2.r - rgb1.r;
  const diffGreen = rgb2.g - rgb1.g;
  const diffBlue = rgb2.b - rgb1.b;
  let rgbRes = {};
  steps = Math.floor(steps);

  for (let i = 0; i < steps; i++) {
    rgbRes = {
      r: Math.floor(rgb2.r - diffRed * (i / steps)),
      g: Math.floor(rgb2.g - diffGreen * (i / steps)),
      b: Math.floor(rgb2.b - diffBlue * (i / steps))
    };
    gradient[steps - i] = rgbRes;
  }

  return gradient;
};

const layerHasAbstractType = (layers, layerId, abstractTypeId) => {
  const layer = find(layers, { id: layerId });
  if (!layer) {
    return false;
  }
  return (
    find(layer.layer_abstract_type_associations, {
      abstract_type_id: abstractTypeId
    }) !== undefined
  );
};

class HeatMap extends Component {
  constructor(props) {
    super(props);

    this.heatmapContainer = React.createRef();
    this.stimuliImage = React.createRef();

    this.imageLoadedHandler = this.imageLoadedHandler.bind(this);

    this.state = {
      isRendering: false,
      imageW: 0,
      imageH: 0,
      containerW: 'auto',
      containerH: 'auto',
      heatmapInstances: []
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!nextProps.stimuli) {
      return {
        imageW: 0,
        imageH: 0
      };
    }

    return null;
  }

  didExistingHeatmapsUpdate(prevHeatmaps, prevColors) {
    if (this.props.heatmaps.length !== prevHeatmaps.length) {
      return true;
    }

    let heatmapsAreDifferent = false;

    for (let i = 0; i < prevHeatmaps.length; i++) {
      const heatmapToCompare = prevHeatmaps[i];

      const newerHeatmap = find(this.props.heatmaps, {
        layerId: heatmapToCompare.layerId,
        stimuliId: heatmapToCompare.stimuliId,
        abstractTypeId: heatmapToCompare.abstractTypeId
      });

      if (!newerHeatmap) {
        heatmapsAreDifferent = true;
        break;
      } else {
        if (newerHeatmap.generationTimestamp !== heatmapToCompare.generationTimestamp) {
          heatmapsAreDifferent = true;
          break;
        }
        const previousHeatmapColors = prevColors[newerHeatmap.layerId][newerHeatmap.abstractTypeId];
        const newerHeatmapColors = this.props.signalsColors[newerHeatmap.layerId][
          newerHeatmap.abstractTypeId
        ];
        if (!isEqual(previousHeatmapColors, newerHeatmapColors)) {
          heatmapsAreDifferent = true;
          break;
        }
      }
    }
    return heatmapsAreDifferent;
  }

  didLayersSortChanged(prevProps) {
    for (let i = 0; i < prevProps.layersSorting.length; i++) {
      if (
        this.props.layersSorting.length > i &&
        this.props.layersSorting[i] !== prevProps.layersSorting[i]
      )
        return true;
    }
    return false;
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (difference(this.props.visibleLayerIds, prevProps.visibleLayerIds).length > 0 ||
        this.didExistingHeatmapsUpdate(prevProps.heatmaps, prevProps.signalsColors) ||
        this.didLayersSortChanged(prevProps)) &&
      this.props.stimuli
    ) {
      this.setState(
        {
          isRendering: true
        },
        () => {
          setTimeout(() => {
            this.doHeatMap();
            this.renderStackedImages();
          }, 0);
        }
      );
    }
  }

  imageLoadedHandler(e) {
    RemoveZoom();
    ApplyZoom(this.heatmapContainer.current);

    const ratio = e.target.naturalWidth / e.target.naturalHeight;
    const newW = this.heatmapContainer.current.offsetWidth;
    const newH = Math.round(newW / ratio);

    this.stimuliImage.current.width = newW;
    this.stimuliImage.current.height = newH;
  }

  createHeatmap(props) {
    const { points, width, height, color, opacity } = props;

    if (points && points.length > 0) {
      const canvas = document.createElement('canvas');

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      let start = new Date().getTime();

      let max = 0;
      let min = 100000;
      each(points, entry => {
        max = Math.max(entry.value, max);
        min = Math.min(entry.value, min);
      });

      const id = ctx.getImageData(0, 0, width, height);

      const gradientEndColor = Color(color)
        .lighten(95)
        .toHexString();
      const gradient = getGradient(color, gradientEndColor, max);

      points.forEach(entry => {
        const value = Math.floor(entry.value);
        if (value > 0) {
          const pos = (entry.y * width + entry.x) * 4;
          const rgb = gradient[value] ? gradient[value] : { r: 0, g: 0, b: 0 };
          id.data[pos] = rgb.r;
          id.data[pos + 1] = rgb.g;
          id.data[pos + 2] = rgb.b;
          id.data[pos + 3] = opacity * 255;
        }
      });
      console.log(`Creating Buffer took: ${new Date().getTime() - start}ms`);
      start = new Date().getTime();
      ctx.putImageData(id, 0, 0);
      const base60Image = canvas.toDataURL();
      console.log(`Writing Canvas took: ${new Date().getTime() - start}ms`);

      const imgNode = document.createElement('img');
      imgNode.draggable = false;
      imgNode.crossOrigin = 'anonymous'; // https://stackoverflow.com/questions/17035106/context-getimagedata-operation-is-insecure
      imgNode.src = base60Image;
      imgNode.className = 'heatmap';
      return imgNode;
    }
  }

  doHeatMap() {
    const self = this;
    const config = {
      container: this.heatmapContainer.current,
      originContainer: {
        width: this.props.stimuli.width,
        height: this.props.stimuli.height
      },
      radius: 1
    };

    const newInstances = [];
    const existingInstances = cloneDeep(self.state.heatmapInstances);
    // config.container.
    each(this.props.heatmaps, heatmap => {
      // Does this one need regeneration ?
      const existingInstanceIndex = findIndex(self.state.heatmapInstances, hInstance => {
        return (
          heatmap.layerId === hInstance.layerId &&
          heatmap.stimuliId === hInstance.stimuliId &&
          heatmap.abstractTypeId === hInstance.abstractTypeId
        );
      });

      const heatmapLayer = find(self.props.layers, { id: heatmap.layerId });
      if (heatmapLayer && heatmap.data.length > 0) {
        const heatmapSignalAssociation = find(heatmapLayer.layer_abstract_type_associations, {
          abstract_type_id: heatmap.abstractTypeId
        });
        const heatmapColors = this.props.signalsColors[heatmap.layerId][heatmap.abstractTypeId];
        const renderColor = [
          heatmapColors.color[0],
          heatmapColors.color[1],
          heatmapColors.color[2]
        ];

        if (heatmapSignalAssociation) {
          if (existingInstanceIndex === -1) {
            const h = {
              colors: renderColor,
              opacity: heatmapColors.opacity,
              stimuliId: heatmap.stimuliId,
              layerId: heatmap.layerId,
              abstractTypeId: heatmap.abstractTypeId,
              instance: this.createHeatmap({
                points: heatmap.data,
                width: config.originContainer.width,
                height: config.originContainer.height,
                color: {
                  r: renderColor[0],
                  g: renderColor[1],
                  b: renderColor[2]
                },
                opacity: heatmapColors.opacity
              }),
              timestamp: heatmap.generationTimestamp
            };

            newInstances.push(h);
          } else {
            const existingInstance = existingInstances[existingInstanceIndex];
            if (
              !isEqual(existingInstance.colors, renderColor) ||
              existingInstance.opacity !== heatmapColors.opacity ||
              heatmap.generationTimestamp !== existingInstance.timestamp
            ) {
              existingInstances[existingInstanceIndex] = {
                colors: renderColor,
                opacity: heatmapColors.opacity,
                stimuliId: heatmap.stimuliId,
                layerId: heatmap.layerId,
                abstractTypeId: heatmap.abstractTypeId,
                instance: this.createHeatmap({
                  points: heatmap.data,
                  width: config.originContainer.width,
                  height: config.originContainer.height,
                  color: {
                    r: renderColor[0],
                    g: renderColor[1],
                    b: renderColor[2]
                  },
                  opacity: heatmapColors.opacity
                }),
                timestamp: heatmap.generationTimestamp
              };
            }
          }
        }
      }
    });

    this.setState({
      heatmapInstances: [...existingInstances, ...newInstances],
      isRendering: false
    });
  }

  getHeatmapImages() {
    const self = this;
    const filteredHeatmaps = filter(this.state.heatmapInstances, h => {
      return (
        find(self.props.heatmaps, {
          layerId: h.layerId,
          stimuliId: h.stimuliId,
          abstractTypeId: h.abstractTypeId
        }) !== undefined
      );
    });

    const sortedHeatmaps = filteredHeatmaps.sort((a, b) => {
      const isADimension = this.props.signalsSorting[a.layerId].indexOf(a.abstractTypeId) < 0;
      const isBDimension = this.props.signalsSorting[b.layerId].indexOf(b.abstractTypeId) < 0;

      let aSignalId = a.abstractTypeId;
      let bSignalId = b.abstractTypeId;

      const aDimensionId = a.abstractTypeId;
      const bDimensionId = b.abstractTypeId;

      if (isADimension) {
        aSignalId = parseInt(
          findKey(this.props.signalDimensionsSorting[a.layerId], signal => {
            return signal.indexOf(a.abstractTypeId) >= 0;
          }),
          10
        );
      }

      if (isBDimension) {
        bSignalId = parseInt(
          findKey(this.props.signalDimensionsSorting[b.layerId], signal => {
            return signal.indexOf(b.abstractTypeId) >= 0;
          }),
          10
        );
      }

      return (
        self.props.layersSorting.indexOf(b.layerId) - self.props.layersSorting.indexOf(a.layerId) ||
        self.props.signalsSorting[b.layerId].indexOf(bSignalId) -
          self.props.signalsSorting[a.layerId].indexOf(aSignalId) ||
        self.props.signalDimensionsSorting[b.layerId][bSignalId].indexOf(bDimensionId) -
          self.props.signalDimensionsSorting[a.layerId][aSignalId].indexOf(aDimensionId)
      );
    });

    return map(sortedHeatmaps, h => {
      return h.instance;
    });
  }

  getStimuliImage() {
    const imgNode = document.createElement('img');
    imgNode.crossOrigin = 'anonymous'; // https://stackoverflow.com/questions/17035106/context-getimagedata-operation-is-insecure
    imgNode.src = this.props.stimuli.image_url;
    return imgNode;
  }

  renderStackedImages() {
    if (!this.heatmapContainer.current) return;
    if (this.state.isRendering) return; // generating heatmaps..

    const heatmapImages = this.getHeatmapImages();

    const images = [this.getStimuliImage(), ...heatmapImages];

    const canvas = document.createElement('canvas');
    // canvas sizes must be set once. If set on every image, it resets the image data
    // and we get only the latest image that is drawed on it
    canvas.width = this.props.stimuli.width;
    canvas.height = this.props.stimuli.height;

    const ctx = canvas.getContext('2d');

    const renderNext = rendererIterator => {
      const renderer = rendererIterator();
      return () => {
        // stopping condition
        if (renderer) {
          const nextRender = renderNext(rendererIterator);
          renderer(nextRender);
        }
      };
    };

    const imageDrawers = map(images, i => {
      return getImageDrawer(ctx, i);
    });

    // add the last drawer that will generate an image on screen
    imageDrawers.push(
      saveCanvasToImage(
        canvas,
        this.stimuliImage.current,
        this.heatmapContainer.current.clientWidth
      )
    );

    const rendererIterator = iterator(imageDrawers);

    const kickstartRenderer = rendererIterator();

    const renderNextCallBack = renderNext(rendererIterator);

    kickstartRenderer(renderNextCallBack);
  }

  render() {
    const imageSrc = this.props.stimuli ? this.props.stimuli.image_url : undefined;
    const containerStyle = {
      width: this.state.containerW,
      height: this.state.containerH
    };

    return (
      <Dimmer.Dimmable>
        <Dimmer active={this.state.isRendering} inverted>
          <Loader size="huge">Generating Heatmap</Loader>
        </Dimmer>
        <div id="visualisationContainer" style={containerStyle} ref={this.heatmapContainer}>
          {imageSrc && (
            <img
              crossOrigin="anonymous" // https://stackoverflow.com/questions/17035106/context-getimagedata-operation-is-insecure
              style={{ visibility: 'visible' }}
              draggable="false"
              ref={this.stimuliImage}
              src={imageSrc}
              onLoad={this.imageLoadedHandler}
              alt="Stimuli"
            />
          )}
        </div>
      </Dimmer.Dimmable>
    );
  }
}

export default connect(state => ({
  visibleLayerIds: state.analysis.single.visibleLayerIds,
  layers: state.analysis.single.layers,
  layersSorting: state.analysis.single.layersSorting,
  signalsColors: state.analysis.single.signalsColors,
  signalsSorting: state.analysis.single.signalsSorting,
  signalDimensionsSorting: state.analysis.single.signalDimensionsSorting,
  heatmaps: (() => {
    const { visibleLayerIds } = state.analysis.single;
    const { selectedStimuliId } = state.analysis.single;

    if (visibleLayerIds.length === 0 || selectedStimuliId === null) {
      return [];
    }

    return filter(state.analysis.data.heatmaps, dataset => {
      return (
        dataset.stimuliId === selectedStimuliId &&
        visibleLayerIds.indexOf(dataset.layerId) >= 0 &&
        layerHasAbstractType(state.analysis.single.layers, dataset.layerId, dataset.abstractTypeId)
      );
    });
  })(),
  stimuli: (() => {
    const { selectedStimuliId } = state.analysis.single;
    return find(state.analysis.single.stimuli, { id: selectedStimuliId });
  })()
}))(HeatMap);



// WEBPACK FOOTER //
// ./src/scenes/analysis/components/analysis/visualisations/HeatMap.js